import { useState, useCallback, useMemo } from "react";
import {
  gisBases,
  authorizations,
  documentReports,
  expirationItems,
  documentTypes,
} from "@/mocks/documentos";
import type { DocumentItem } from "@/mocks/documentos";
import { useDocumentsList } from "@/hooks";
import DocumentsKpiCard from "./components/DocumentsKpiCard";
import DocumentsFilters from "./components/DocumentsFilters";
import DocumentsTable from "./components/DocumentsTable";
import DocumentCard from "./components/DocumentCard";
import DocumentDetailDrawer from "./components/DocumentDetailDrawer";
import NewDocumentModal from "./components/NewDocumentModal";
import DocumentQualityPanel from "./components/DocumentQualityPanel";
import ProjectSourcesPanel from "./components/ProjectSourcesPanel";
import TreeSourcesTable from "./components/TreeSourcesTable";
import EnvironmentalSourcesTable from "./components/EnvironmentalSourcesTable";
import GisSourcesTable from "./components/GisSourcesTable";
import GisQualityPanel from "./components/GisQualityPanel";
import AuthorizationsTable from "./components/AuthorizationsTable";
import ExpirationPanel from "./components/ExpirationPanel";
import DocumentReportsGrid from "./components/DocumentReportsGrid";

const tabs = [
  { id: "biblioteca", label: "Biblioteca" },
  { id: "projetos", label: "Fontes por Projeto" },
  { id: "arvores", label: "Fontes por Árvore CTA" },
  { id: "ambientais", label: "Fontes Ambientais" },
  { id: "gis", label: "Bases GIS" },
  { id: "autorizacoes", label: "Autorizações e Laudos" },
  { id: "vencimentos", label: "Vencimentos" },
  { id: "relatorios", label: "Relatórios" },
];

const initialFilters = {
  search: "",
  type: "",
  project: "",
  municipality: "",
  status: "",
  hasBinding: "",
  hasFile: "",
  isExpired: "",
};

export default function Documentos() {
  const [activeTab, setActiveTab] = useState("biblioteca");
  const [selectedDoc, setSelectedDoc] = useState<DocumentItem | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const { items: documents } = useDocumentsList();

  const handleViewDocument = useCallback((docOrId: DocumentItem | string) => {
    if (typeof docOrId === "string") {
      const found = documents.find((d) => d.id === docOrId);
      if (found) setSelectedDoc(found);
    } else {
      setSelectedDoc(docOrId);
    }
  }, [documents]);

  const handleCloseDrawer = useCallback(() => {
    setSelectedDoc(null);
  }, []);

  const handleNewDocument = useCallback(() => {
    setShowNewModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowNewModal(false);
  }, []);

  const handleSubmitNew = useCallback((data: Record<string, string>) => {
    // In a real app, this would save to backend
    console.log("Novo documento:", data);
  }, []);

  const filteredDocs = useMemo(() => {
    return documents.filter((doc) => {
      if (filters.search) {
        const s = filters.search.toLowerCase();
        const matchesSearch =
          doc.title.toLowerCase().includes(s) ||
          doc.number.toLowerCase().includes(s) ||
          doc.primaryBinding.toLowerCase().includes(s) ||
          doc.responsible.toLowerCase().includes(s) ||
          doc.description.toLowerCase().includes(s);
        if (!matchesSearch) return false;
      }
      if (filters.type && doc.type !== filters.type) return false;
      if (filters.status && doc.status !== filters.status) return false;
      if (filters.isExpired === "yes" && doc.status !== "Vencido") return false;
      if (filters.isExpired === "no" && doc.status === "Vencido") return false;
      if (filters.hasFile === "yes" && !doc.hasFile) return false;
      if (filters.hasFile === "no" && doc.hasFile) return false;
      if (filters.hasBinding === "yes" && doc.primaryBindingType === "none") return false;
      if (filters.hasBinding === "no" && doc.primaryBindingType !== "none") return false;
      if (filters.project && doc.projectName !== filters.project) return false;
      if (filters.municipality && !doc.municipality.toLowerCase().includes(filters.municipality.toLowerCase())) return false;
      return true;
    });
  }, [filters]);

  const validatedDocs = documents.filter((d) => d.status === "Validado").length;
  const pendingDocs = documents.filter((d) => d.status === "Em revisão" || d.status === "Rascunho").length;
  const expiredDocs = documents.filter((d) => d.status === "Vencido").length;
  const technicalReports = documents.filter((d) => d.type.includes("Laudo")).length;
  const gisDocs = documents.filter((d) =>
    d.type === "Shapefile" || d.type === "GeoJSON" || d.type === "Base cartográfica" || d.type === "Ortofoto" || d.type === "Mapa PDF"
  ).length;
  const authDocs = authorizations.length;
  const unlinkedDocs = documents.filter((d) => d.primaryBindingType === "none").length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="text-xs text-foreground-500 mb-1">Dashboard &gt; Documentos e Fontes</div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
            Documentos e Fontes
          </h1>
          <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
            Organize fontes documentais, laudos, autorizações, bases cartográficas e evidências vinculadas ao CTA, projetos, áreas verdes e responsabilidades ambientais.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleNewDocument}
            className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Novo documento
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors whitespace-nowrap">
            <i className="ri-upload-cloud-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Upload em lote
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-accent-700 bg-accent-50 rounded-lg hover:bg-accent-100 transition-colors whitespace-nowrap">
            <i className="ri-map-2-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Importar base cartográfica
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
            <i className="ri-file-chart-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Gerar relatório
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
            <i className="ri-download-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Exportar índice
          </button>
        </div>
      </div>

      {/* Conceptual notice */}
      <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-secondary-600 w-full h-full flex items-center justify-center text-lg" />
          </div>
          <p className="text-xs text-secondary-700 leading-relaxed">
            Cada documento pode funcionar como <strong>fonte oficial</strong> para árvores, áreas ambientais, vistorias, autorizações, restrições e responsabilidades dentro do modelo <strong>LADM-FloraIQ</strong> — conectando Source (fonte documental), Party (parte envolvida), Spatial Unit (unidade ambiental) e RRR (responsabilidades, restrições e autorizações).
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3 md:gap-4">
        <DocumentsKpiCard label="Documentos cadastrados" value="2.846" icon="ri-folder-line" color="primary" subtitle="Total no repositório" />
        <DocumentsKpiCard label="Fontes validadas" value={String(validatedDocs)} icon="ri-check-double-line" color="primary" subtitle="Documentos conferidos" />
        <DocumentsKpiCard label="Pendentes de revisão" value={String(pendingDocs)} icon="ri-search-line" color="warning" subtitle="Aguardando validação" />
        <DocumentsKpiCard label="Documentos vencidos" value={String(expiredDocs)} icon="ri-error-warning-line" color="danger" subtitle="Necessitam renovação" />
        <DocumentsKpiCard label="Laudos técnicos" value={String(technicalReports)} icon="ri-microscope-line" color="accent" subtitle="Fitossanitários e pareceres" />
        <DocumentsKpiCard label="Bases GIS" value={String(gisDocs)} icon="ri-map-line" color="accent" subtitle="Shapefiles, GeoJSON, raster" />
        <DocumentsKpiCard label="Autorizações ambientais" value={String(authDocs)} icon="ri-shield-check-line" color="secondary" subtitle="Podas, supressões, transplantes" />
        <DocumentsKpiCard label="Documentos sem vínculo" value={String(unlinkedDocs)} icon="ri-link-unlink-m" color="warning" subtitle="Precisam ser vinculados" />
      </div>

      {/* Tabs */}
      <div className="border-b border-background-200">
        <div className="flex gap-1 overflow-x-auto pb-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors border-b-2 ${
                activeTab === tab.id
                  ? "text-primary-700 border-primary-500 bg-primary-50/50"
                  : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {/* Biblioteca */}
        {activeTab === "biblioteca" && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground-900">Biblioteca documental</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCards(false)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!showCards ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                >
                  <i className="ri-table-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Tabela
                </button>
                <button
                  onClick={() => setShowCards(true)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${showCards ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                >
                  <i className="ri-grid-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Cards
                </button>
              </div>
            </div>

            <DocumentsFilters filters={filters} onChange={setFilters} />

            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-filter-line w-4 h-4 flex items-center justify-center" />
              <span>{filteredDocs.length} de {documents.length} documentos encontrados</span>
            </div>

            {showCards ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredDocs.map((doc) => (
                  <DocumentCard key={doc.id} document={doc} onView={handleViewDocument} />
                ))}
              </div>
            ) : (
              <DocumentsTable documents={filteredDocs} onView={handleViewDocument} />
            )}

            {filteredDocs.length === 0 && (
              <div className="text-center py-12 text-foreground-400">
                <i className="ri-file-search-line text-4xl mb-3 block" />
                <p className="text-sm">Nenhum documento encontrado com os filtros atuais.</p>
              </div>
            )}
          </div>
        )}

        {/* Fontes por Projeto */}
        {activeTab === "projetos" && (
          <ProjectSourcesPanel onViewDocument={handleViewDocument} />
        )}

        {/* Fontes por Árvore CTA */}
        {activeTab === "arvores" && (
          <TreeSourcesTable onViewDocument={handleViewDocument} />
        )}

        {/* Fontes Ambientais */}
        {activeTab === "ambientais" && (
          <EnvironmentalSourcesTable onViewDocument={handleViewDocument} />
        )}

        {/* Bases GIS */}
        {activeTab === "gis" && (
          <div className="space-y-5">
            <GisSourcesTable />
            <GisQualityPanel />
          </div>
        )}

        {/* Autorizações e Laudos */}
        {activeTab === "autorizacoes" && (
          <AuthorizationsTable />
        )}

        {/* Vencimentos */}
        {activeTab === "vencimentos" && (
          <ExpirationPanel />
        )}

        {/* Relatórios */}
        {activeTab === "relatorios" && (
          <div className="space-y-5">
            <DocumentReportsGrid reports={documentReports} />
            <DocumentQualityPanel />
          </div>
        )}
      </div>

      {/* Drawer */}
      {selectedDoc && (
        <DocumentDetailDrawer document={selectedDoc} onClose={handleCloseDrawer} />
      )}

      {/* New Document Modal */}
      {showNewModal && (
        <NewDocumentModal onClose={handleCloseModal} onSubmit={handleSubmitNew} />
      )}
    </div>
  );
}